import {useCallback, useEffect, useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {ProjectListItem} from '../../common/api/generated/model/projectListItem'
import type {StorageUsageResponse} from '../../common/api/generated/model/storageUsageResponse'
import type {UserResponse} from '../../common/api/generated/model/userResponse'
import {
  deleteMyProject,
  listMyProjects,
  updateMyProject,
} from '../../common/api/generated/endpoints/my-projects/my-projects'
import {getMe} from '../../common/api/generated/endpoints/auth/auth'
import {listProjectTags} from '../../common/api/generated/endpoints/projects/projects'
import {getMyUsage} from '../../common/api/generated/endpoints/storage/storage'
import {useAuth} from '../auth/useAuth'
import {useToast} from '../../common/components/toast/toastContext'
import {handleApiError} from '../../common/errors/handleApiError'
import {trackEvent} from '../../common/analytics/trackEvent'
import LoadingLogo from '../components/LoadingLogo'
import MyProjectCard from '../components/myProjects/MyProjectCard'
import ConfirmModal from '../components/myProjects/ConfirmModal'
import Icon from '../components/myProjects/Icon'
import './TemplatesPage.css'
import './MyProjectsPage.css'

const formatMB = (bytes: number): string => (bytes / (1024 * 1024)).toFixed(1)

const storageTone = (percent: number): string => {
  if (percent >= 95) return 'is-danger'
  if (percent >= 80) return 'is-warn'
  return 'is-ok'
}

const ProjectsPage = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {user} = useAuth()
  const toast = useToast()

  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [storageUsage, setStorageUsage] = useState<StorageUsageResponse | null>(null)
  const [me, setMe] = useState<UserResponse | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | null>(null)
  const [shareTarget, setShareTarget] = useState<ProjectListItem | null>(null)
  const [allTags, setAllTags] = useState<string[]>([])

  // Stable mount timestamp so card "x days ago" stays pure across re-renders.
  const [nowMs] = useState(() => Date.now())

  const fetchProjects = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const {data} = await listMyProjects()
      setProjects(data)
    } catch (err) {
      const msg = handleApiError(err, 'projects.list')
      toast.error(msg ?? t('projects.loadError'))
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [t, toast])

  const fetchStorageUsage = useCallback(async () => {
    try {
      const {data} = await getMyUsage()
      setStorageUsage(data)
    } catch (err) {
      handleApiError(err, 'projects.storageUsage')
      // Non-blocking stat — don't toast, breadcrumb is enough
    }
  }, [])

  const fetchMe = useCallback(async () => {
    try {
      const {data} = await getMe()
      setMe(data)
    } catch (err) {
      handleApiError(err, 'projects.me')
      // Non-blocking — don't toast
    }
  }, [])

  const fetchAllTags = useCallback(async () => {
    try {
      const {data} = await listProjectTags()
      setAllTags(data)
    } catch (err) {
      handleApiError(err, 'projects.tagsList')
      // Suggestions are optional — silent on failure
    }
  }, [])

  useEffect(() => {
    fetchProjects()
    fetchStorageUsage()
    fetchMe()
    fetchAllTags()
  }, [fetchProjects, fetchStorageUsage, fetchMe, fetchAllTags])

  // Poll every 15s while any thumbnail is stale, like the previous version.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasStaleThumbnails = useCallback(
    (list: ProjectListItem[]) => list.some((p) => p.sceneThumbnails.some((thumb) => thumb.stale)),
    [],
  )
  useEffect(() => {
    if (loading) return
    if (!hasStaleThumbnails(projects)) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    if (pollRef.current) return

    pollRef.current = setInterval(async () => {
      try {
        const {data} = await listMyProjects()
        setProjects(data)
        if (!hasStaleThumbnails(data) && pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch (err) {
        handleApiError(err, 'projects.pollThumbnails')
        // Polling failure — breadcrumb only, no toast spam
      }
    }, 15_000)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [loading, projects, hasStaleThumbnails])

  // Optimistic local patch
  const patchProject = useCallback((id: string, patch: Partial<ProjectListItem>) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? {...p, ...patch} : p)))
  }, [])

  const handleOpen = useCallback(
    (project: ProjectListItem) => {
      trackEvent('project_card_opened', {project_id: project.id})
      navigate(`/editor/${project.id}`)
    },
    [navigate],
  )

  const handleRename = useCallback(
    async (project: ProjectListItem, name: string) => {
      if (name === project.name) return
      patchProject(project.id, {name})
      try {
        await updateMyProject(project.id, {name})
      } catch (err) {
        patchProject(project.id, {name: project.name})
        toast.error(handleApiError(err, 'projects.rename') ?? t('projects.renameFailed'))
      }
    },
    [patchProject, t, toast],
  )

  const handleDescriptionChange = useCallback(
    async (project: ProjectListItem, description: string) => {
      const next = description.length === 0 ? null : description
      if (next === project.description) return
      patchProject(project.id, {description: next})
      try {
        await updateMyProject(project.id, {description: next})
      } catch (err) {
        patchProject(project.id, {description: project.description})
        toast.error(handleApiError(err, 'projects.description') ?? t('projects.updateFailed'))
      }
    },
    [patchProject, t, toast],
  )

  const handleTagsChange = useCallback(
    async (project: ProjectListItem, tags: string[]) => {
      const same = tags.length === project.tags.length && tags.every((tg, i) => tg === project.tags[i])
      if (same) return
      patchProject(project.id, {tags})
      try {
        await updateMyProject(project.id, {tags})
        // Re-fetch the authoritative public-tag list so newly added tags appear
        // and tags that no longer belong to any public project drop out.
        fetchAllTags()
      } catch (err) {
        patchProject(project.id, {tags: project.tags})
        toast.error(handleApiError(err, 'projects.tags') ?? t('projects.updateFailed'))
      }
    },
    [fetchAllTags, patchProject, t, toast],
  )

  const handleShareConfirm = useCallback(
    async (project: ProjectListItem) => {
      const nextPublic = !project.isPublic
      patchProject(project.id, {isPublic: nextPublic, isFree: nextPublic ? false : project.isFree})
      setShareTarget(null)
      try {
        await updateMyProject(project.id, {isPublic: nextPublic})
        trackEvent('project_share_toggled', {new_state: nextPublic ? 'public' : 'private'})
      } catch (err) {
        patchProject(project.id, {isPublic: project.isPublic, isFree: project.isFree})
        toast.error(handleApiError(err, 'projects.share') ?? t('projects.shareFailed'))
      }
    },
    [patchProject, t, toast],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    const id = deleteTarget.id
    setDeleteTarget(null)
    try {
      await deleteMyProject(id)
      setProjects((prev) => prev.filter((p) => p.id !== id))
      fetchStorageUsage()
      fetchAllTags()
      trackEvent('project_delete_confirmed')
    } catch (err) {
      toast.error(handleApiError(err, 'projects.delete') ?? t('projects.deleteFailed'))
    }
  }, [deleteTarget, fetchAllTags, fetchStorageUsage, t, toast])

  const totalScenes = projects.reduce((sum, p) => sum + p.sceneCount, 0)
  const publicCount = projects.filter((p) => p.isPublic).length
  const firstName =
    me?.firstName?.trim() ||
    (user?.displayName ?? '').split(' ')[0] ||
    me?.email?.split('@')[0] ||
    user?.email?.split('@')[0] ||
    ''
  const credits = me?.credits ?? null

  return (
    <div className="templates-root my-projects-root">
      <div className="bg-grid" />
      <div className="noise" />

      <main className="projects-page">
        <header className="mp-head">
          <span className="mp-eyebrow">{t('myProjects.eyebrow')}</span>
          <div className="mp-head-row">
            <div>
              <h1 className="mp-title">
                {firstName ? (
                  <>
                    {t('myProjects.welcomePrefix')} <em>{firstName}</em>.
                  </>
                ) : (
                  t('myProjects.welcomeFallback')
                )}
              </h1>
              <p className="mp-sub">{t('myProjects.subtitle')}</p>
            </div>
          </div>
        </header>

        <div className="mp-stats">
          <div className="mp-stat mp-stat-storage">
            <span className="mp-stat-label">{t('myProjects.stats.storage')}</span>
            {storageUsage ? (
              <>
                <span className="mp-stat-value">
                  {formatMB(storageUsage.usedBytes)}
                  <span className="unit">
                    {t('myProjects.stats.storageOf', {total: formatMB(storageUsage.quotaBytes)})}
                  </span>
                </span>
                <div
                  className={`mp-storage-track ${storageTone(storageUsage.usedPercent)}`}
                  role="progressbar"
                  aria-valuenow={Math.round(storageUsage.usedPercent)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="mp-storage-fill" style={{width: `${Math.min(100, storageUsage.usedPercent)}%`}} />
                </div>
              </>
            ) : (
              <span className="mp-stat-value">—</span>
            )}
          </div>
          <div className="mp-stat">
            <span className="mp-stat-label">{t('myProjects.stats.projects')}</span>
            <span className="mp-stat-value">{projects.length}</span>
          </div>
          <div className="mp-stat">
            <span className="mp-stat-label">{t('myProjects.stats.scenesTotal')}</span>
            <span className="mp-stat-value">{totalScenes}</span>
          </div>
          <div className="mp-stat">
            <span className="mp-stat-label">{t('myProjects.stats.publicProjects')}</span>
            <span className="mp-stat-value">{publicCount}</span>
          </div>
          <div className="mp-stat mp-stat-credits">
            <span className="mp-stat-label">{t('myProjects.stats.renderCredits')}</span>
            <div className="mp-stat-row">
              <span className="mp-stat-value">
                {credits ?? '—'}
                <span className="unit">{t('myProjects.stats.creditsLeft')}</span>
              </span>
              <Link to="/pricing" className="mp-buy-more">
                {t('myProjects.stats.buyMore')}
              </Link>
            </div>
          </div>
        </div>

        <Link to="/render-history" className="mp-hint">
          <span className="mp-hint-ico">
            <Icon name="cloudDownload" size={22} />
          </span>
          <span className="mp-hint-body">
            <strong>{t('myProjects.hint.title')}</strong>
            <span>{t('myProjects.hint.body')}</span>
          </span>
          <span className="mp-hint-cta">
            {t('myProjects.hint.cta')} <Icon name="arrow" size={14} />
          </span>
        </Link>

        {loading ? (
          <div className="mp-loading">
            <LoadingLogo size={56} />
          </div>
        ) : loadError && projects.length === 0 ? (
          <div className="mp-empty">
            <h3>{t('projects.loadError')}</h3>
            <button type="button" onClick={fetchProjects} className="mp-empty-cta">
              {t('projects.retry')}
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className="mp-empty">
            <h3>{t('myProjects.empty.title')}</h3>
            <p>{t('myProjects.empty.body')}</p>
            <Link
              to="/editor/new"
              className="mp-empty-cta"
              onClick={() => trackEvent('new_project_clicked', {location: 'empty_state'})}
            >
              <span className="plus">+</span> {t('myProjects.empty.cta')}
            </Link>
          </div>
        ) : (
          <div className="tpl-cards">
            {projects.map((project) => (
              <MyProjectCard
                key={project.id}
                project={project}
                nowMs={nowMs}
                allTags={allTags}
                onOpen={handleOpen}
                onRename={handleRename}
                onDescriptionChange={handleDescriptionChange}
                onTagsChange={handleTagsChange}
                onShareClick={setShareTarget}
                onDeleteClick={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </main>

      <ConfirmModal
        open={!!deleteTarget}
        title={t('myProjects.delete.title', {name: deleteTarget?.name ?? ''})}
        body={<p>{t('myProjects.delete.body')}</p>}
        confirmLabel={t('myProjects.delete.confirm')}
        cancelLabel={t('myProjects.delete.cancel')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmModal
        open={!!shareTarget}
        title={
          shareTarget?.isPublic
            ? t('myProjects.share.publicTitle', {name: shareTarget?.name ?? ''})
            : t('myProjects.share.publishTitle', {name: shareTarget?.name ?? ''})
        }
        body={
          shareTarget?.isPublic ? (
            <p>{t('myProjects.share.publicBody', {count: shareTarget?.usageCount ?? 0})}</p>
          ) : (
            <>
              <p>{t('myProjects.share.publishBody')}</p>
              <p className="mp-modal-notice">{t('myProjects.share.publishNotice')}</p>
            </>
          )
        }
        badge={
          shareTarget?.isPublic ? (
            <span className="mp-modal-badge mp-modal-badge-public">
              <Icon name="globe" size={13} /> {t('myProjects.share.currentlyPublic')}
            </span>
          ) : undefined
        }
        confirmLabel={
          shareTarget?.isPublic ? t('myProjects.share.unshareConfirm') : t('myProjects.share.publishConfirm')
        }
        cancelLabel={t('myProjects.share.cancel')}
        variant={shareTarget?.isPublic ? 'danger' : 'primary'}
        onConfirm={() => shareTarget && handleShareConfirm(shareTarget)}
        onClose={() => setShareTarget(null)}
      />
    </div>
  )
}

export default ProjectsPage
